import { useMemo, useState } from 'react'

import { List, Section } from '@factorialco/deprecated-design-system'
import { DeleteOverlapPeriodDocument } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import DeletePeriodModal from 'modules/work_schedules/components/DeletePeriodModal'
import type OverlapPeriod from 'modules/work_schedules/store/models/OverlapPeriod'

import BlankSlate from './BlankSlate'
import Item from './Item'

const PeriodSection = ({ id, overlapPeriods }: { id: string; overlapPeriods: OverlapPeriod[] }) => {
  const periods = useMemo(
    () =>
      overlapPeriods.filter(
        (period) => period.attributes.schedule_id === id && !period.attributes.default
      ),
    [id, overlapPeriods]
  )
  const { navigation } = useNavigation()
  const [periodToDelete, setPeriodToDelete] = useState<string | null>(null)

  const [deleteOverlapPeriod] = useMutation(DeleteOverlapPeriodDocument, {
    extractorFn: (data) => data.workScheduleMutations.deleteWorkscheduleOverlapPeriod,
    update: (cache, { data }) => {
      const deletedPeriodId =
        data?.workScheduleMutations.deleteWorkscheduleOverlapPeriod.overlapPeriod?.id

      if (!deletedPeriodId) {
        return
      }

      cache.modify({
        id: cache.identify({ __typename: 'WorkScheduleSchedule', id }),
        fields: {
          overlapPeriods(existingRefs = [], { readField }) {
            return existingRefs.filter((item: { __ref?: string }) => readField('id', item) !== deletedPeriodId)
          },
        },
      })

      cache.evict({
        id: cache.identify({ __typename: 'WorkScheduleOverlapPeriod', id: deletedPeriodId }),
      })
      cache.gc()
    },
  })

  const handleDeletePeriod = (periodId: string) => {
    setPeriodToDelete(periodId)
  }

  const confirmDeletePeriod = async (closePortal: () => void) => {
    if (!periodToDelete) return

    const result = await deleteOverlapPeriod({
      variables: { id: periodToDelete },
    })

    const handled = handleResult(result, () => {})

    if (handled.success) {
      closePortal()
    }
  }

  const closeModal = () => {
    setPeriodToDelete(null)
  }

  return (
    <>
      <Section.TwoThirds
        illustration="browser-edit"
        title={i18n.t('settings.work_schedule.period_section.title')}
        description={i18n.t('settings.work_schedule.period_section.description')}
        action={{
          label: i18n.t('settings.work_schedule.period_section.add'),
          to: navigation.settings.workSchedule.id.periodNew.fullPath({ id: id }),
        }}
      >
        <List>
          {!periods.length ? (
            <BlankSlate />
          ) : (
            periods.map((period, idx) => (
              <List.Item key={period.attributes.id}>
                <Item
                  itemName={`${i18n.t('settings.work_schedule.period_section.period')} ${idx + 1}`}
                  period={period}
                  onDeletePeriod={handleDeletePeriod}
                />
              </List.Item>
            ))
          )}
        </List>
      </Section.TwoThirds>

      {periodToDelete && <DeletePeriodModal onClose={closeModal} onConfirm={confirmDeletePeriod} />}
    </>
  )
}

export default observer(PeriodSection)
